<template>
  <div class="flex flex-col h-full w-full px-4 overflow-y-auto md:max-w-[600px]">
    <!-- The accrual only runs when a visit is closed, so spell that out here —
         owners who never press "סיום תור" see an empty club and assume it's broken. -->
    <div class="flex items-start gap-2.5 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <i class="pi pi-info-circle text-blue-600 text-lg mt-0.5"></i>
      <div class="flex flex-col gap-1">
        <span class="font-medium text-sm">איך נצברות ההטבות?</span>
        <span class="text-xs text-surface-600">
          הצבירה ללקוח מתבצעת בסיום התור — יש ללחוץ על "סיום תור" בתור לאחר שהסתיים.
          תורים שלא נסגרו כך לא ייחשבו בתוכנית.
        </span>
      </div>
    </div>

    <settings-toggle
      class="mb-1"
      :status="loyaltyProgramSettings.birthday.shouldCollect ?? false"
      title="תאריך יום הולדת"
      description="האם לבקש מהלקוח את תאריך יום ההולדת? נשתמש בזה כדי לשלוח הודעות יום הולדת שמח, ואם תרצו גם הטבה כלשהי. וכמובן נעדכן אתכם בעת ביקור על יום הולדת מתקרב :)"
      @value-changed="(val) => {loyaltyProgramSettings.birthday.shouldCollect = val}"/>
    <div v-if="loyaltyProgramSettings.birthday.shouldCollect" class="flex items-center justify-between mt-3 p-3 bg-surface-50 rounded-lg border border-surface-200">
      <div class="flex items-start gap-2.5">
        <i class="pi pi-gift text-primary-600 text-lg mt-0.5"></i>
        <div class="flex flex-col gap-1">
          <span class="font-medium text-sm">ברכות יום הולדת אוטומטיות</span>
          <span class="text-xs text-surface-500">ניתן להפעיל שליחה אוטומטית של ברכות יום הולדת ללקוחות דרך עמוד האוטומציות.</span>
        </div>
      </div>
      <Button
        label="הגדרה"
        size="small"
        text
        icon="pi pi-arrow-left"
        iconPos="right"
        @click="goToAutomations"
      />
    </div>

    <divider/>
    <div class="w-full flex flex-col">
      <label class="font-medium">שליחת הודעות המועדון</label>
      <span class="text-sm mb-2">איך לשלוח ללקוחות את ההזמנה להצטרף למועדון ואת הודעת ההצטרפות</span>
      <settings-card>
        <settings-radio-group
          :modelValue="loyaltyProgramSettings.messagesChannel ?? 'sms'"
          last
          :options="[
            { value: 'sms', label: 'SMS', desc: 'ההודעות נשלחות בהודעת SMS' },
            { value: 'whatsapp', label: 'WhatsApp', desc: 'הודעת WhatsApp עם כפתור להצטרפות · אם ההודעה לא נמסרת, נשלח SMS במקומה' },
          ]"
          @update:modelValue="(v) => loyaltyProgramSettings.messagesChannel = v"
        />
      </settings-card>
    </div>

    <divider/>
    <div v-if="loyaltyProgramSettings.promoType === 'points'" class="w-full flex flex-col">
      <label class="font-medium">אחוז הצבירה</label>
      <span class="text-sm mb-2">על כל תור ששולם הלקוח יצבור נקודות, לפי האחוז שתקבעו בשלב זה</span>
      <InputNumber
        v-model="loyaltyProgramSettings.pointsOptions.percentageToGather"
        :max="50"
        :min="1"
        suffix="%"
        class="mb-2"/>

      <Slider
        v-model="loyaltyProgramSettings.pointsOptions.percentageToGather"
        :max="50"
        :min="1"
        class="w-full mt-2"/>

      <span class="font-medium mt-4 mb-2">איך לקרוא לנקודות? </span>
      <InputText v-model="loyaltyProgramSettings.pointsOptions.pointName" class="mb-2"/>
    </div>

    <div v-if="loyaltyProgramSettings.promoType === 'discount'" class="w-full flex flex-col">
      <label class="font-medium">מספר ביקורים לקבלת הנחה</label>
      <span class="text-sm mb-2">לאחר מספר הביקורים הלקוח יהיה זכאי להנחה שתגדירו</span>
      <div class="w-full relative">
        <InputNumber
          v-model="loyaltyProgramSettings.discountOptions.numberOfAppointments"
          :max="12"
          :min="2"
          class="mb-2 w-full"/>
        <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ביקורים</span>
      </div>

      <Slider
        v-model="loyaltyProgramSettings.discountOptions.numberOfAppointments"
        :max="12"
        :min="2"
        class="mx-2  mt-2"/>

      <label class="font-medium mt-3">סכום ההנחה על הביקור המוזל</label>
      <span class="text-sm mb-2">לאחר מספר הביקורים הלקוח יהיה זכאי לסכום ההנחה המוזן</span>
      <div class="w-full relative">
        <InputNumber
          v-model="loyaltyProgramSettings.discountOptions.discountAmount"
          :max="100"
          :min="1"
          prefix="% "
          class="mb-2 w-full"/>
        <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">הנחה</span>
      </div>
      <Slider
        v-model="loyaltyProgramSettings.discountOptions.discountAmount"
        :max="100"
        :min="1"
        class="mx-2  mt-2"/>

      <span class="font-medium mt-4 mb-2">שירותים הכלולים בהנחה </span>
      <span class="text-sm mb-2">לאחר מספר הביקורים הלקוח יהיה זכאי לסכום ההנחה המוזן עבור השירותים הנבחרים</span>
      <select-service
        v-model="promoLimitedTo"
        @services-selected="onServicesSelected"
      />
    </div>

    <Button
      label=" ביטול התוכנית "
      severity="danger"
      text
      class="mt-8 self-center flex-none"
      size="small"
      @click="deactivateLoyaltyProgram"/>

    <settings-save-bar
      :dirty="dirty"
      :saved="saved"
      :loading="ctaLoading"
      :disabled="ctaDisabled"
      @save="updateLoyaltyProgram"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, reactive, ref, watch} from "vue";
import {ILoyaltyProgram, IService, LoyaltyProgramPromosTypes} from "@torim-app/types";
import {useAdminStore} from "@app/stores";
import {AdminRouteNames} from "@app/router/route-names";
import {LoyaltyProgramApi} from "@app/api/admin/loyalty-program-api";
import {useToast} from "vue-toastification";
import {useRouter} from "vue-router";
import { trackEvent } from '@app/composables/posthog';
import SettingsSaveBar from '../configurations/ui/SettingsSaveBar.vue';
import SettingsCard from '../configurations/ui/SettingsCard.vue';
import SettingsRadioGroup from '../configurations/ui/SettingsRadioGroup.vue';
import { useSettingsForm } from '../configurations/ui/settings-form';

const adminStore = useAdminStore();
const toast = useToast();
const router = useRouter();

adminStore.setPage(AdminRouteNames.LoyaltyProgram);

const goToAutomations = () => {
  router.push({ name: AdminRouteNames.Communication, query: { tab: '0' } });
};

const ctaLoading = ref<boolean>(false);
const loyaltyProgramSettings = reactive<ILoyaltyProgram>(<ILoyaltyProgram><unknown>{
  discountOptions: {discountAmount: 50, numberOfAppointments: 5, promoLimitedTo: []},
  pointsOptions: {pointName: 'נקודות', percentageToGather: 5}
});

const { dirty, saved, takeSnapshot, flashSaved } = useSettingsForm(() => loyaltyProgramSettings);

const services = computed<IService[]>(() => adminStore.getServices);
const promoLimitedTo = computed<IService[]>(() => services.value?.filter(s => loyaltyProgramSettings?.discountOptions?.promoLimitedTo?.includes(s.id)));
const ctaDisabled = computed<boolean>(() => {
  if (loyaltyProgramSettings.promoType === 'points')
    return !loyaltyProgramSettings.pointsOptions?.percentageToGather;
  else
    return !loyaltyProgramSettings.discountOptions?.discountAmount ||
      !loyaltyProgramSettings.discountOptions?.numberOfAppointments ||
      !loyaltyProgramSettings.discountOptions?.promoLimitedTo?.length
});

const onServicesSelected = (services: IService[]) => {
  loyaltyProgramSettings.discountOptions.promoLimitedTo.splice(0);
  loyaltyProgramSettings.discountOptions.promoLimitedTo.push(...services.map(service => service.id));
};

const updateLoyaltyProgram = () => {
  if (ctaDisabled.value || ctaLoading.value) return;
  trackEvent('loyalty_settings_saved');
  ctaLoading.value = true;

  const updatedSettings = {...loyaltyProgramSettings};
  if (updatedSettings.promoType === 'discount')
    updatedSettings.pointsOptions = undefined;
  else
    updatedSettings.discountOptions = undefined;

  LoyaltyProgramApi.updateProgram(updatedSettings)
    .then(() => {
      flashSaved();
      adminStore.fetchBusinessData();
    })
    .catch(() => {
      toast.error('לא הצלחנו לעדכן את התוכנית, אנא נסו שוב')
    }).finally(() => {
      ctaLoading.value = false;
    })
}

const deactivateLoyaltyProgram = () => {
  LoyaltyProgramApi.deactivateProgram()
    .then(() => {
      toast.success('התוכנית בוטלה בהצלחה');
      adminStore.fetchBusinessData();
      router.push({name: AdminRouteNames.Home});
    })
    .catch(() => {
      toast.error('לא הצלחנו לבטל את התוכנית, אנא נסו שוב')
    })
}

Object.assign(loyaltyProgramSettings, adminStore.businessSiteData.loyaltyProgram);
// The channel is stored on the business, not the program document
loyaltyProgramSettings.messagesChannel = adminStore.businessSiteData.configurations?.loyaltyMessagesChannel ?? 'sms';

// Snapshot the settled initial state (after the birthday watcher may seed the greeting)
// so the save bar only appears once the user actually changes something.
onMounted(() => nextTick(() => takeSnapshot()));

watch(() => loyaltyProgramSettings.birthday, () => {
  if (loyaltyProgramSettings.birthday.shouldCollect && !loyaltyProgramSettings.birthday.greetingMessage)
    loyaltyProgramSettings.birthday.greetingMessage = `המון מזל טוב ויום הולדת שמח, מ${adminStore.businessSiteData.title} 🎂`
  else if (!loyaltyProgramSettings.birthday.shouldCollect && loyaltyProgramSettings.birthday.greetingMessage)
    loyaltyProgramSettings.birthday.greetingMessage = undefined;
})

watch(() => loyaltyProgramSettings.promoType, () => {
  if (loyaltyProgramSettings.promoType === LoyaltyProgramPromosTypes.Point && !loyaltyProgramSettings.pointsOptions) {
    loyaltyProgramSettings.pointsOptions = {pointName: 'נקודות', percentageToGather: 5}
  }
})

defineExpose({ updateLoyaltyProgram, ctaLoading, ctaDisabled, dirty });

</script>

<style scoped lang="scss">

</style>
